import { Box } from '@chakra-ui/react'
import { extractUUID } from '@helper/extractUUID'
import { isEmpty } from 'lodash'
import Image from 'next/image'
import React, { useEffect, useState } from 'react'
type Props = {
  src: string
  errorImage?: string
  // All other props
  [x: string]: any
}

const uploadCareCDNBase: string = 'https://ucarecdn.com/'

// const uploadCareResize: string = '-/smart_resize/'

export const FallbackImage: React.FC<Props> = ({
  src,
  dimensions,
  errorImage,
  ...rest
}) => {
  const [imgSrc, setImgSrc] = useState<any>(src)
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const brokenImage: string = errorImage
    ? errorImage
    : '/assets/placeholder-img.svg'
  const loadingImage: string = '/assets/image-shimmer.gif'
  useEffect(() => {
    setImgSrc(src)
  }, [src])
  const restProps = { ...rest }
  if (restProps?.objectFit) {
    restProps.style = {
      ...(restProps?.style ?? {}),
      objectFit: restProps.objectFit,
    }
    delete restProps.objectFit
  }
  if (!dimensions) {
    restProps.style = { ...restProps?.style, width: '100%', height: '100%' }
  }
  return (
    // <div style={{ position: 'relative', ...dimensions }}>
    <Image
      {...restProps}
      alt={rest.alt ? rest.alt : 'image'}
      src={imgSrc ? imgSrc : isLoading ? loadingImage : brokenImage}
      placeholder={isLoading ? 'blur' : 'empty'}
      blurDataURL={isLoading ? loadingImage : ''}
      {...dimensions}
      fill={!dimensions}
      sizes="(max-width: 768px) 100vw,
              (max-width: 1200px) 50vw,
              33vw"
      onLoad={() => {
        setIsLoading(false)
      }}
      onError={() => {
        setImgSrc(brokenImage)
      }}
    />
    // </div>
  )
}

const uploadCareAsset = (src: string, options?: any): string => {
  if (!src) return ''
  let url = ''
  if (src?.startsWith('https:')) {
    if (src?.includes('/-/')) {
      const uuid = extractUUID(src)
      url = `${uploadCareCDNBase}${uuid}/`
    } else {
      url = `${src}`
    }
  } else {
    url = `${uploadCareCDNBase}${src}/`
  }

  options = { ...options, resize: 'x2160' }

  if (options && Object.keys(options).length == 0) return url

  // trim trailing slash
  url = url.replace(/\/$/, '')
  if (options) {
    Object.keys(options).forEach((key) => {
      url += !isEmpty(options[key]) ? `/-/${key}/${options[key]}` : `/-/${key}`
      // url += `/-/${key}/${options[key]}`
    })
  }
  return `${url}/`
}

type DisplayUploadCareAssetProps = {
  // All other props
  src: string
  type?: string
  resize?: string
  dimensions?: {
    width?: number[] | number
    height?: number[] | number
  }
  options?: any
  video?: any
  [x: string]: any
}

const DisplayUploadCareAsset: React.FC<DisplayUploadCareAssetProps> = (
  props,
) => {
  const {
    src,
    type = 'IMAGE',
    resize = '300x300',
    dimensions,
    options,
    video,
    ...rest
  } = props
  const height = dimensions?.height
    ? Array.isArray(dimensions?.height)
      ? dimensions?.height?.map((h) => `${h}px`)
      : `${dimensions?.height}px`
    : '100%'
  const width = dimensions?.width
    ? Array.isArray(dimensions?.width)
      ? dimensions?.width?.map((w) => `${w}px`)
      : `${dimensions?.width}px`
    : '100%'
  return (
    <Box position={'relative'} overflow="hidden" width={width} height={height}>
      <BaseUploadCareAsset
        src={src}
        type={type}
        video={{
          controls: true,
          muted: true,
          autoPlay: true,
          loop: true,
          width: width,
          height: height,
          style: {
            height: '100%',
            width: '100%',
            objectFit: 'contain',
          },
          ...video,
        }}
        options={options}
        objectFit={'contain'}
        alt={'featured'}
        {...rest}
      />
    </Box>
  )
}

type BaseUploadCareAssetProps = Omit<DisplayUploadCareAssetProps, 'dimensions'>

const BaseUploadCareAsset: React.FC<BaseUploadCareAssetProps> = (props) => {
  const {
    src,
    type = 'IMAGE',
    resize = '300x300',
    dimensions,
    options,
    video,
    ...rest
  } = props
  let url = ''
  let media_type = type
  let mediaOptions = options || {}
  let videoOptions = {
    ...{ autoPlay: true, loop: true, muted: true, playsInline: true },
    ...(video || {}),
  }
  if (src?.includes('/video/') && type == 'IMAGE') {
    media_type = 'VIDEO'
  }
  if (
    src?.includes('.mp4') &&
    ['IMAGE', 'CAROUSEL_ALBUM'].includes(media_type)
  ) {
    media_type = 'VIDEO'
  }
  if (!src?.includes('instagram') && media_type == 'IMAGE') {
    src && (url = uploadCareAsset(src, mediaOptions))
  }
  if (media_type === 'IMAGE') return <FallbackImage {...rest} src={url} />
  return (
    <video
      {...videoOptions}
      {...dimensions}
      style={{ objectFit: 'contain', ...videoOptions?.style }}
    >
      <source src={src} />
    </video>
  )
}

function getImage(deviceWidth: number, uuid: any, options?: any) {
  const fixedDimensions = {
    mobile: {
      width: 450,
      height: 370,
    },
    desktop: {
      width: 1460,
      height: 400,
    },
  }
  const dimensions =
    deviceWidth < 480 ? fixedDimensions.mobile : fixedDimensions.desktop
  const defaultOptions = {
    scale_crop: `${dimensions.width}x${dimensions.height}`,
    stretch: 'fill',
    brightness: '-20',
    quality: 'smart_retina',
  }

  // console.log('defaultOptions', defaultOptions)
  return uploadCareAsset(uuid, options ? options : defaultOptions)
}

export { DisplayUploadCareAsset, getImage, uploadCareAsset }
